import { motion } from "framer-motion";
import { Factory, ArrowUpRight } from "lucide-react";
import { HUB_NODES, OTHER_NODES, STAT_ITEMS } from "../../lib/supplierNetwork";

const PANEL_MOTION = { initial: { opacity: 0, y: 20 }, whileInView: { opacity: 1, y: 0 }, viewport: { once: true }, transition: { duration: 0.5 } };
const CARD_MOTION = { initial: { opacity: 0, x: 20 }, whileInView: { opacity: 1, x: 0 }, viewport: { once: true } };
const MAX_SUPPLIERS = Math.max(...HUB_NODES.map((n) => n.suppliers));

function StatStrip() {
  return (
    <motion.div {...PANEL_MOTION} className="grid grid-cols-3 rounded-2xl border border-ib-steel/20 bg-ib-surface divide-x divide-ib-steel/20" data-testid="sourcing-stats">
      {STAT_ITEMS.map(({ icon: Icon, value, label }) => (
        <div key={label} className="p-4">
          <Icon className="w-4 h-4 text-ib-blue mb-3" />
          <div className="font-outfit text-2xl font-extrabold text-ib-text tracking-tight">{value}</div>
          <div className="font-manrope text-[11px] text-ib-text-muted mt-0.5">{label}</div>
        </div>
      ))}
    </motion.div>
  );
}

function HubCard({ node, active, onHover }) {
  const isActive = active === node.id;
  return (
    <motion.div {...CARD_MOTION} transition={{ duration: 0.4, delay: node.delay }}
      onMouseEnter={() => onHover(node.id)} onMouseLeave={() => onHover(null)}
      className={`group relative rounded-xl border p-4 flex items-center gap-4 cursor-pointer transition-colors duration-200 ${isActive ? "border-ib-blue/60 bg-ib-blue/5" : "border-ib-steel/20 bg-ib-surface hover:border-ib-blue/40"}`}
      data-testid={`state-card-${node.id}`}>
      <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 transition-colors ${isActive ? "bg-ib-blue text-white" : "bg-ib-blue/10 text-ib-blue"}`}>
        <Factory className="w-4 h-4" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5">
          <span className="font-outfit font-bold text-sm text-ib-text">{node.label}</span>
          <ArrowUpRight className={`w-3.5 h-3.5 text-ib-blue transition-opacity ${isActive ? "opacity-100" : "opacity-0"}`} />
        </div>
        <div className="font-jetbrains text-[10px] text-ib-text-muted">{node.city}</div>
        <div className="mt-2 h-1 rounded-full bg-ib-steel/15 overflow-hidden">
          <motion.div className="h-full rounded-full bg-ib-blue" initial={{ width: 0 }} whileInView={{ width: `${(node.suppliers / MAX_SUPPLIERS) * 100}%` }} viewport={{ once: true }} transition={{ duration: 0.8, delay: node.delay + 0.2 }} />
        </div>
      </div>
      <div className="text-right flex-shrink-0">
        <div className="font-outfit text-xl font-extrabold text-ib-text leading-none">{node.suppliers}</div>
        <div className="font-jetbrains text-[9px] text-ib-text-muted mt-1">suppliers</div>
      </div>
    </motion.div>
  );
}

export function SupplierPanel({ active, onHover }) {
  return (
    <div className="lg:col-span-2 space-y-4">
      <StatStrip />
      <div className="font-jetbrains text-[10px] text-ib-text-muted uppercase tracking-widest pt-2">Major steel hubs</div>
      <div className="space-y-2.5">{HUB_NODES.map((n) => <HubCard key={n.id} node={n} active={active} onHover={onHover} />)}</div>
      <motion.div {...PANEL_MOTION} className="rounded-2xl border border-ib-steel/20 bg-ib-surface p-4">
        <div className="font-jetbrains text-[10px] text-ib-text-muted uppercase tracking-widest mb-3">Also serving</div>
        <div className="flex flex-wrap gap-2">
          {OTHER_NODES.map((n) => (
            <button key={n.id} type="button" onMouseEnter={() => onHover(n.id)} onMouseLeave={() => onHover(null)} data-testid={`serving-chip-${n.id}`}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-manrope border transition-colors ${active === n.id ? "border-ib-blue text-ib-blue bg-ib-blue/5" : "border-ib-steel/20 text-ib-text-secondary hover:border-ib-blue/40"}`}>
              <span className="w-1.5 h-1.5 rounded-full bg-ib-blue" />{n.label}
            </button>
          ))}
        </div>
      </motion.div>
    </div>
  );
}
